import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import Dropdown from "../dropdown/Dropdown";
import clsx from "clsx";
import {useFocus} from "presentation/common/hooks/useFocus";
import OpenSelectorArrowIcon from "../../icons/OpenSelectorArrowIcon";
import PropTypes from "prop-types";
import Input from "../input/Input";
import {emptyFn} from "presentation/common/helpers/utils";

import styles from './SimpleSelect.module.scss';

const SimpleSelect = ({items, value, className, valueBind, labelBind, onChange, onBlur, disabled = false, name, ...other}) => {
  const focusRef = useRef();
  const firstUpdate = useRef(true);
  const [focus, setFocus] = useFocus(focusRef);

  const [itemSelected] = items.filter(item => item[valueBind] === value);
  // state showing if dropdown is open or closed
  const [dropdown, setDropdown] = useState(false);

  const toggleDropdown = useCallback(() => {
    if(disabled) return;
    setDropdown(!dropdown);
  }, [setDropdown, dropdown, disabled]);

  const onSelectItem = useCallback((item) => {
    setFocus(false);

    if (!itemSelected || itemSelected[valueBind] !== item[valueBind]) {
      onChange(item);
    }
  }, [itemSelected, onChange, valueBind, setFocus]);

  useEffect(() => {
    if (!focus && dropdown) {
      setDropdown(false);
    }
  }, [focus]);

  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    onBlur({
      target: {
        name,
      },
    })
  }, [dropdown]);

  return (
    <Input {...other}>
      <div className={clsx('relative', className)} ref={focusRef} tabIndex={0}>
        <div className="w-full flex flex-col items-center mx-auto">
          <div className="w-full">
            <div className="flex flex-col items-center relative">
              <div className="w-full">
                <div
                  className={clsx("cursor-pointer flex bg-white outline-none", {[styles.inputInvalid]: other.error, [styles.disabled]: disabled}, styles.input)}
                  onClick={toggleDropdown}>
                  <div
                    className="flex flex-auto flex-wrap text-xs font-normal leading-none max-w-full items-center pl-2">
                    {itemSelected && itemSelected[labelBind]}
                  </div>
                  <div className="text-gray-300 w-8 pl-2 pr-1 flex items-center">
                    <button className="w-6 h-6 text-gray-600 outline-none focus:outline-none">
                      <OpenSelectorArrowIcon/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {dropdown
              ? <Dropdown
                list={items}
                onSelectItem={onSelectItem}
                itemSelected={itemSelected}
                valueBind={valueBind}
                labelBind={labelBind}/>
              : null
            }
          </div>
        </div>
      </div>
    </Input>
  )
};

SimpleSelect.defaultProps = {
  valueBind: 'id',
  labelBind: 'name',
  items: [],
  onChange: emptyFn
};

SimpleSelect.propTypes = {
  valueBind: PropTypes.string,
  labelBind: PropTypes.string,
  items: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func
};

export default React.memo(SimpleSelect);
